"use client";

import { Link as RouterLink } from "react-router";
import type { Area } from "@shared/permissions";
import { siteHref } from "../current";
import Link from "../link";
import { usePathname } from "../navigation";
import { logout } from "../actions";
import { TasmaWordmark } from "../brand/TasmaWordmark";

const TABS: { href: string; label: string; area?: Area }[] = [
  { href: "/admin", label: "Overview" },
  { href: "/admin/publish", label: "Publish", area: "settings" },
  { href: "/admin/stats", label: "Statistics", area: "stats" },
  { href: "/admin/leads", label: "Leads", area: "leads" },
  { href: "/admin/scores", label: "Game scores", area: "scores" },
  { href: "/admin/blog", label: "Blog", area: "content" },
  { href: "/admin/projects", label: "Projects", area: "content" },
  { href: "/admin/services", label: "Services", area: "content" },
  { href: "/admin/faq", label: "FAQ", area: "content" },
  { href: "/admin/pages", label: "Page text", area: "content" },
  { href: "/admin/interface", label: "Interface text", area: "content" },
  { href: "/admin/settings", label: "Settings", area: "settings" },
  { href: "/admin/media", label: "Media", area: "media" },
  { href: "/admin/history", label: "History", area: "history" },
];

const active = (href: string, pathname: string) => (href === "/admin" ? pathname === "/admin" : pathname === href || pathname.startsWith(`${href}/`));

// Desktop: a Midnight sidebar. Phones and tablets: a top bar with tabs you can swipe sideways. Only the tabs this
// person may see on this website (their permissions from the hub).
export function AdminNav({ siteName, can }: { siteName: string; can: (area: Area) => boolean }) {
  const pathname = usePathname();
  return (
    <header className="sticky top-0 z-30 bg-midnight text-offwhite lg:h-svh lg:overflow-y-auto">
      <div className="flex items-center justify-between gap-3 px-4 pt-3 lg:flex-col lg:items-start lg:px-6 lg:pt-8">
        <Link href="/admin" aria-label="Admin overview" className="py-1">
          <TasmaWordmark tone="offwhite-amber" className="w-24 lg:w-32" />
        </Link>
        <span className="font-mono text-[0.65rem] tracking-[0.25em] text-offwhite/70 uppercase lg:mt-1">{siteName}</span>
      </div>
      <nav aria-label="Admin" className="mt-2 overflow-x-auto px-2 pb-2 [scrollbar-width:none] lg:mt-8 lg:overflow-visible lg:px-3">
        <ul className="flex gap-1 lg:flex-col">
          {TABS.filter((tab) => !tab.area || can(tab.area)).map((tab) => {
            const on = active(tab.href, pathname);
            return (
              <li key={tab.href} className="shrink-0">
                <Link
                  href={tab.href}
                  aria-current={on ? "page" : undefined}
                  className={`flex min-h-11 items-center rounded-lg px-3 text-sm font-bold whitespace-nowrap transition-colors lg:px-4 ${on ? "bg-offwhite text-midnight" : "text-offwhite/80 hover:bg-offwhite/10 hover:text-offwhite"}`}
                >
                  {tab.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      <div className="hidden flex-col gap-1 px-3 pt-6 pb-8 lg:flex">
        <RouterLink to="/" className="flex min-h-11 items-center rounded-lg px-4 text-sm text-offwhite/80 hover:bg-offwhite/10">
          ← All websites
        </RouterLink>
        <a href={siteHref("/")} target="_blank" rel="noreferrer" className="flex min-h-11 items-center rounded-lg px-4 text-sm text-offwhite/80 hover:bg-offwhite/10">
          View website ↗
        </a>
        <form action={logout}>
          <button type="submit" className="flex min-h-11 w-full items-center rounded-lg px-4 text-sm text-offwhite/80 hover:bg-offwhite/10">
            Sign out
          </button>
        </form>
      </div>
    </header>
  );
}

/** Phone/tablet footer row with the same two links the desktop sidebar has. */
export function AdminFooterLinks() {
  return (
    <div className="mt-12 flex flex-wrap gap-4 border-t border-midnight/10 pt-6 text-sm lg:hidden">
      <RouterLink to="/" className="py-2 text-teal underline underline-offset-4">
        ← All websites
      </RouterLink>
      <a href={siteHref("/")} target="_blank" rel="noreferrer" className="py-2 text-teal underline underline-offset-4">
        View website ↗
      </a>
      <form action={logout}>
        <button type="submit" className="py-2 text-midnight/70 underline underline-offset-4">
          Sign out
        </button>
      </form>
    </div>
  );
}
